import type { DirectionsLeg, RoutePlan } from '../api/types';
import type { RouteImages } from './mapCapture';
import { shapeOf, type TurnShape } from '../util/turnShape';

/**
 * A planned route, written out to be read away from the screen.
 *
 * Everything here works from a plan the caller already holds plus the turns
 * fetched once for the occasion, so the same six writers serve the planner
 * and the saved-routes list without either knowing about the other.
 *
 * Nothing is fetched, nothing is uploaded: the file is built in the browser
 * and handed straight to the person who asked for it. That is what "offline"
 * has to mean for a route somebody is about to drive out of signal with.
 */

export type ExportFormat =
  | 'pdf' | 'gpx' | 'gpxroute' | 'geojson' | 'markdown' | 'json';

/**
 * One planned day: a round, its turns, and its pictures.
 *
 * A single-day route is an operation of one. Writing it that way means the
 * six writers below have one shape to handle rather than two, and an operator
 * carrying a week gets the week in one file instead of opening five.
 */
export interface ExportDay {
  /** "Day 2 - Sat 10 Oct", or empty on a route that is not part of one. */
  label: string;
  /**
   * YYYY-MM-DD, for turning the schedule's clock times into real instants.
   *
   * The plan carries "09:03" and nothing else; a GPX timestamp needs a date
   * to hang it on. Absent falls back to the day the plan was made, which is
   * right for a round planned for today and a day out for one planned ahead
   * - better than no times at all, and nothing but a viewer's pace chart
   * depends on it.
   */
  date?: string;
  plan: RoutePlan;
  /** The turns, leg by leg. Empty is allowed and says so in the file. */
  directions: DirectionsLeg[];
  /**
   * Pictures of this day, for the formats that can hold one.
   *
   * Only the PDF uses them, and only when the page that asked for the export
   * had a map to borrow. Absent is a fine answer: the file is then the same
   * document without the plates.
   */
  images?: RouteImages;
}

export interface ExportSource {
  /** What to call the route or the operation, and the file. */
  name: string;
  /** Every planned day, in order. One entry for a single-day route. */
  days: ExportDay[];
}

/*
  Every format this can write, and which of them are currently offered.

  All six still work — `exportRoute` writes any of them, and the tests and
  the writers below are untouched. Only the PDF is put in front of anybody
  for now; the rest are held back until they have been through the same
  checking it has. Offering one again is this flag and nothing else.
*/
export const EXPORT_FORMATS: {
  key: ExportFormat; label: string; hint: string; offered: boolean;
}[] = [
  { key: 'pdf', label: 'PDF', hint: 'To print or carry', offered: true },
  { key: 'gpx', label: 'GPX', hint: 'Satnav and handheld units', offered: false },
  { key: 'gpxroute', label: 'GPX (route)', hint: 'Google My Maps', offered: false },
  { key: 'geojson', label: 'GeoJSON', hint: 'Mapping tools and GIS', offered: false },
  { key: 'markdown', label: 'Markdown', hint: 'Plain text to paste', offered: false },
  { key: 'json', label: 'JSON', hint: 'Everything, for another system', offered: false },
];

/** What the export button lists. See `offered` above. */
export const OFFERED_EXPORT_FORMATS = EXPORT_FORMATS.filter((f) => f.offered);

const EXTENSION: Record<ExportFormat, string> = {
  pdf: 'pdf', gpx: 'gpx', gpxroute: 'gpx', geojson: 'geojson', markdown: 'md', json: 'json',
};

/*
  What to add to the filename where the extension alone would collide.

  Both GPX files are .gpx, and a reader asked to compare them should not have
  to work out which of "route.gpx" and "route (1).gpx" is which.
*/
const FILE_SUFFIX: Partial<Record<ExportFormat, string>> = { gpxroute: '-route' };

const MEDIA: Record<ExportFormat, string> = {
  pdf: 'application/pdf',
  gpx: 'application/gpx+xml',
  gpxroute: 'application/gpx+xml',
  geojson: 'application/geo+json',
  markdown: 'text/markdown;charset=utf-8',
  json: 'application/json',
};

// ── small shared readings ───────────────────────────────────────────

/** Metres as the itinerary says them, so a file never disagrees with it. */
function km(metres: number): string {
  return `${(metres / 1000).toFixed(1)} km`;
}

/**
 * A departure or an arrival, the way the itinerary says it.
 *
 * The plan carries "14:05" because that is what the server works in. Nobody
 * here reads a schedule that way, and a file that disagrees with the screen
 * it was exported from is a file somebody has to check.
 */
function clock(hhmm: string): string {
  const [h = NaN, m = NaN] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const hour = ((h + 11) % 12) + 1;

  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function minutes(mins: number): string {
  const whole = Math.round(mins);
  if (whole < 60) return `${whole} min`;

  return `${Math.floor(whole / 60)} hr${whole % 60 ? ` ${whole % 60} min` : ''}`;
}

/**
 * S, E, or the stop's number.
 *
 * The same marks the map and the itinerary use. A file that numbered its
 * stops differently would be a second itinerary to reconcile.
 */
function stopMark(plan: RoutePlan, index: number): string {
  if (index === 0) return 'S';
  if (index === plan.stops.length - 1) return 'E';

  return String(index);
}

/**
 * What to say about a stop's clock, which is not the same at both ends.
 *
 * The start is departed from, never arrived at: "Arrive 09:00, leave 09:00"
 * is two readings of one moment and says nothing. The finish is arrived at
 * and not left. Only the stops in between have all three.
 */
function stopTimes(plan: RoutePlan, index: number): string {
  const stop = plan.stops[index];
  if (!stop) return '';
  if (index === 0) return `Depart ${clock(stop.departureTime)}`;
  if (index === plan.stops.length - 1) return `Arrive ${clock(stop.arrivalTime)}`;

  return `Arrive ${clock(stop.arrivalTime)}`
    + (stop.dwellMinutes ? `, stay ${minutes(stop.dwellMinutes)}` : '')
    + `, leave ${clock(stop.departureTime)}`;
}

/** A filename nobody has to rename before saving it. */
function fileNameFor(name: string, format: ExportFormat): string {
  const stem = (name + (FILE_SUFFIX[format] ?? '') || 'route')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 60) || 'route';

  return `${stem}.${EXTENSION[format]}`;
}

/** The turns for one leg, or an empty list where there are none. */
function turnsFor(directions: DirectionsLeg[], legIndex: number) {
  return directions.find((d) => d.fromIndex === legIndex)?.maneuvers ?? [];
}

// ── the writers ─────────────────────────────────────────────────────

function asJson({ days, name }: ExportSource): string {
  /*
    Everything, including the drawn line.

    This is the format for handing the route to another system, so holding
    geometry back to keep the file small would be withholding the only part
    that cannot be reconstructed from the rest.
  */
  return `${JSON.stringify({
    name,
    generatedAt: days[0]?.plan.generatedAt ?? null,
    days: days.map(({ label, plan, directions }) => ({
      label,
      mode: plan.mode,
      startTime: plan.startTime,
      totals: plan.totals,
      stops: plan.stops,
      legs: plan.legs.map((leg, i) => ({ ...leg, maneuvers: turnsFor(directions, i) })),
      avoidedZones: plan.avoidedZones,
      warnings: plan.warnings,
    })),
  }, null, 2)}\n`;
}

/**
 * The properties every feature carries, whether or not it has them.
 *
 * One schema for the whole file. GIS readers build a table from the first
 * feature and expect the rest to match it: a stop with `arrival` followed by
 * a line with `from` is two different tables in one layer, which is what
 * "properties must be defined" means when a reader refuses the file.
 *
 * No arrays and no nulls. An array inside a property is not a column, so the
 * street names and the per-leg instructions are joined into text; and a
 * column that is null in every feature a reader samples has no type it can
 * infer, so "not applicable" is written as an empty string or a zero.
 */
interface FeatureProps {
  routeName: string;
  generatedAt: string;
  day: number;
  dayLabel: string;
  /** start | stop | end | leg | maneuver. */
  kind: string;
  mark: string;
  name: string;
  arrival: string;
  departure: string;
  dwellMinutes: number;
  category: string;
  note: string;
  fromStop: string;
  toStop: string;
  mode: string;
  distanceMeters: number;
  durationMinutes: number;
  leg: number;
  step: number;
  instruction: string;
  streets: string;
}

function asGeoJson({ days, name }: ExportSource): string {
  const generatedAt = days[0]?.plan.generatedAt ?? '';

  const blank = (
    day: number, dayLabel: string, kind: string,
  ): FeatureProps => ({
    routeName: name,
    generatedAt,
    day,
    dayLabel,
    kind,
    mark: '',
    name: '',
    arrival: '',
    departure: '',
    dwellMinutes: 0,
    category: '',
    note: '',
    fromStop: '',
    toStop: '',
    mode: '',
    distanceMeters: 0,
    durationMinutes: 0,
    leg: -1,
    step: 0,
    instruction: '',
    streets: '',
  });

  const features: unknown[] = [];

  // Every feature says which day it belongs to, so a week of them can be
  // split again by whatever reads the file.
  days.forEach(({ label, plan, directions }, dayIndex) => {
    const dayNo = dayIndex + 1;

    plan.stops.forEach((stop, i) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [stop.lng, stop.lat] },
        properties: {
          ...blank(
            dayNo,
            label,
            i === 0 ? 'start' : i === plan.stops.length - 1 ? 'end' : 'stop',
          ),
          mark: stopMark(plan, i),
          name: stop.name,
          arrival: i === 0 ? '' : stop.arrivalTime,
          departure: i === plan.stops.length - 1 ? '' : stop.departureTime,
          dwellMinutes: stop.dwellMinutes,
          category: stop.categoryKey ?? '',
          note: stop.note ?? '',
        },
      });
    });

    plan.legs.forEach((leg, i) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: leg.geometry },
        properties: {
          ...blank(dayNo, label, 'leg'),
          leg: i,
          name: `${plan.stops[leg.fromIndex]?.name ?? ''} to ${plan.stops[leg.toIndex]?.name ?? ''}`,
          fromStop: plan.stops[leg.fromIndex]?.name ?? '',
          toStop: plan.stops[leg.toIndex]?.name ?? '',
          mode: leg.mode,
          distanceMeters: leg.distanceMeters,
          durationMinutes: leg.durationMinutes,
          instruction: turnsFor(directions, i).map((m) => m.instruction).join(' '),
        },
      });
    });

    /*
      The turns as points of their own, not only as a list on the leg.

      A GIS reader can then draw them; folded into the line's properties they
      are a run-on sentence nothing can place.
    */
    directions.forEach((leg) => {
      leg.maneuvers.forEach((m, n) => {
        if (!m.lat && !m.lng) return;
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [m.lng, m.lat] },
          properties: {
            ...blank(dayNo, label, 'maneuver'),
            leg: leg.fromIndex,
            step: n + 1,
            name: m.instruction,
            instruction: m.instruction,
            streets: m.streetNames.join(' / '),
            distanceMeters: m.distanceMeters,
          },
        });
      });
    });
  });

  /*
    No members beyond the two the spec defines.

    The name and the time used to sit on a `properties` member of the
    FeatureCollection itself, which RFC 7946 does not define — readers either
    drop it or object. Both now ride on every feature, where they are a
    column like any other.
  */
  return `${JSON.stringify({ type: 'FeatureCollection', features }, null, 2)}\n`;
}

/** Metres between two points, near enough for pacing a line. */
function metresBetween(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(b[0] - a[0]) * Math.cos(rad((a[1] + b[1]) / 2));

  return Math.sqrt(dLat * dLat + dLng * dLng) * R;
}

/**
 * A clock time on a given day, as an instant.
 *
 * Local time, converted by the browser, so a round that departs at nine
 * reads as nine to whoever planned it.
 */
function instant(date: string, hhmm: string): Date | null {
  const [h = NaN, m = NaN] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const at = new Date(`${date}T00:00:00`);
  if (Number.isNaN(at.getTime())) return null;
  at.setHours(h, m, 0, 0);

  return at;
}

/** XML text, with the five characters that would otherwise end the document. */
function xml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function asGpx({ days, name }: ExportSource, kind: 'track' | 'route' = 'track'): string {
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="TravPlaces" xmlns="http://www.topografix.com/GPX/1/1">',
    '  <metadata>',
    `    <name>${xml(name)}</name>`,
    `    <time>${xml(days[0]?.plan.generatedAt ?? '')}</time>`,
    '  </metadata>',
  ];

  /*
    One line per day, and the stops as waypoints. Nothing else.

    It used to write a <rte> of the turns as well as the <trk> of the drawn
    shape, on the reasoning that handheld units disagree about which they
    follow. In a reader that takes both — Google My Maps, for one — that is
    two routes on one map: the track following the roads, and on top of it
    the route drawn as straight lines hopping from one turn to the next,
    cutting across the city wherever two turns are far apart. The endpoints
    came out twice over as well, once from the waypoints and once from the
    route.

    The shape is the thing a map must get right, so one line stays and the
    other goes. The words are not lost: each turn is attached to the point it
    happens at, where a reader can show it and nothing can draw it as a
    second line.

    `kind` decides which element carries that line, and it exists for one
    viewer. Google My Maps drops its own marker on the first and last point
    of a <trk> and names them "Start of ..." and "End of ...", so a round
    with two stops arrives showing four pins. It does not do this for a
    <rte>. Both files hold the same coordinates; the track is what a satnav
    expects, and the route is for reading on a map that labels track ends.
  */
  days.forEach(({ label, plan, directions, date }) => {
    // Day 2's "S" is not Day 1's, so the mark alone would collide.
    const tag = label ? `${label} ` : '';

    /*
      The day the schedule happens on.

      A plan carries clock times and no date; a GPX timestamp needs both.
      Without them a viewer reports "no time data" and no pace, which is
      most of what a GPX viewer is for.
    */
    const on = date ?? plan.generatedAt.slice(0, 10);
    const when = (hhmm: string) => instant(on, hhmm);

    plan.stops.forEach((stop, i) => {
      /*
        Schema order, which GPX is strict about: time before name, name
        before desc, desc before type. A validating reader refuses any
        other arrangement.
      */
      const at = when(i === 0 ? stop.departureTime : stop.arrivalTime);
      lines.push(
        `  <wpt lat="${stop.lat}" lon="${stop.lng}">`,
        ...(at ? [`    <time>${at.toISOString()}</time>`] : []),
        `    <name>${xml(`${tag}${stopMark(plan, i)} ${stop.name}`)}</name>`,
        `    <desc>${xml(stopTimes(plan, i))}</desc>`,
        '    <type>stop</type>',
        '  </wpt>',
      );
    });

    const place = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

    const open = kind === 'route' ? '  <rte>' : '  <trk>';
    const close = kind === 'route' ? '  </rte>' : '  </trk>';
    const point = kind === 'route' ? 'rtept' : 'trkpt';

    lines.push(open, `    <name>${xml(tag ? label : name)}</name>`);
    plan.legs.forEach((leg, legIndex) => {
      /*
        Where each turn happens, by coordinate, within THIS leg.

        Built per leg rather than once for the day: one leg ends where the
        next begins, so a map keyed across the whole round puts the last
        leg's "your destination is on the right" on the first leg's opening
        point. The maneuver's position was read off this very shape on the
        server, so the coordinates match exactly; rounding guards against the
        round trip through JSON changing the last digit.
      */
      const at = new Map<string, string>();
      turnsFor(directions, legIndex).forEach((m) => {
        if (!m.lat && !m.lng) return;
        at.set(place(m.lat, m.lng), m.instruction);
      });

      /*
        A route has no segments: <rte> holds its points directly. A track
        keeps one segment per leg, which is how a reader tells where a stop
        interrupted the driving.
      */
      /*
        A timestamp per point, paced along the leg by distance.

        The schedule knows when each leg leaves and when it arrives; the
        geometry knows how far along each point is. Between them every point
        gets an instant, which is what turns "no time data" into a moving
        time and an average speed. The stays fall out for free: the next leg
        starts at the departure time, so a thirty-minute stop is thirty
        minutes in which nothing moves.
      */
      const from = when(plan.stops[leg.fromIndex]?.departureTime ?? '');
      const to = when(plan.stops[leg.toIndex]?.arrivalTime ?? '');
      const spans = from && to ? to.getTime() - from.getTime() : 0;

      let run = 0;
      const along = leg.geometry.map((p, n) => {
        const back = leg.geometry[n - 1];
        run += back ? metresBetween(back, p) : 0;

        return run;
      });
      const total = run;

      if (kind === 'track') lines.push('    <trkseg>');
      leg.geometry.forEach(([lng, lat], n) => {
        const share = total > 0 ? (along[n] ?? 0) / total : 0;
        const stamp = from && spans > 0
          ? new Date(from.getTime() + share * spans).toISOString()
          : null;
        const turn = at.get(place(lat, lng));

        if (!stamp && !turn) {
          lines.push(`      <${point} lat="${lat}" lon="${lng}"></${point}>`);

          return;
        }
        lines.push(
          `      <${point} lat="${lat}" lon="${lng}">`,
          ...(stamp ? [`        <time>${stamp}</time>`] : []),
          ...(turn ? [`        <desc>${xml(turn)}</desc>`] : []),
          `      </${point}>`,
        );
      });
      if (kind === 'track') lines.push('    </trkseg>');
    });
    lines.push(close);
  });

  lines.push('</gpx>', '');

  return lines.join('\n');
}

function asMarkdown({ days, name }: ExportSource): string {
  const out: string[] = [`# ${name}`, ''];

  const first = days[0];
  if (days.length > 1 && first) {
    const total = days.reduce((n, d) => n + d.plan.totals.totalMinutes, 0);
    const far = days.reduce((n, d) => n + d.plan.totals.distanceMeters, 0);
    out.push(
      `**${days.length} days** · ${minutes(total)} · ${km(far)}`,
      '',
      `_Planned ${new Date(first.plan.generatedAt).toLocaleString()}_`,
      '',
    );
  }

  days.forEach(({ label, plan, directions }) => {
  if (days.length > 1) out.push(`---`, '', `# ${label}`, '');

  out.push(
    `**${plan.mode}** · departs ${clock(plan.startTime)} · `
    + `${minutes(plan.totals.totalMinutes)} · ${km(plan.totals.distanceMeters)} · `
    + `${Math.max(plan.stops.length - 2, 0)} stops`,
    '',
  );
  if (days.length === 1) {
    out.push(`_Planned ${new Date(plan.generatedAt).toLocaleString()}_`, '');
  }

  if (plan.warnings.length > 0) {
    out.push('> ' + plan.warnings.join('  \n> '), '');
  }

  plan.stops.forEach((stop, i) => {
    out.push(
      `## ${stopMark(plan, i)} · ${stop.name}`,
      '',
      stopTimes(plan, i),
      '',
      `\`${stop.lat.toFixed(6)}, ${stop.lng.toFixed(6)}\``,
      '',
    );
    if (stop.note) out.push(`_${stop.note}_`, '');

    const leg = plan.legs[i];
    if (!leg) return;

    /*
      The same heading the PDF gives a leg, marks and all.

      Markdown is UTF-8, so the arrow is simply an arrow here — it is only the
      PDF, with its WinAnsi fonts, that has to draw one.
    */
    out.push(
      `### ${stopMark(plan, leg.fromIndex)} → ${stopMark(plan, leg.toIndex)}`
      + ` · ${plan.stops[leg.toIndex]?.name ?? 'next stop'}`,
      '',
      `${km(leg.distanceMeters)} · ${minutes(leg.durationMinutes)} by ${leg.mode}`,
      '',
    );

    const turns = turnsFor(directions, i);
    if (turns.length === 0) {
      out.push('_No turn-by-turn for this leg._', '');

      return;
    }
    turns.forEach((m, n) => {
      out.push(`${n + 1}. ${m.instruction}${m.distanceMeters ? ` — ${km(m.distanceMeters)}` : ''}`);
    });
    out.push('');
  });

  if (plan.avoidedZones.length > 0) {
    out.push('## Kept clear of', '');
    plan.avoidedZones.forEach((z) => out.push(`- ${z.name}${z.violated ? ' (not honoured)' : ''}`));
    out.push('');
  }
  });

  return out.join('\n');
}

/**
 * Text the built-in fonts can actually set.
 *
 * jsPDF's Helvetica is WinAnsi. Hand it a character outside that — an arrow
 * in an auto-named route, say — and it quietly switches the whole string to a
 * two-byte encoding the font cannot draw, which comes out as widely spaced
 * nonsense. Every string in this document goes through here.
 */
function safe(text: string): string {
  return text
    /*
      Drawn as a real arrow where there is room for one - see `arrow` below.
      In running text, where there is not, the word beats two hyphens.
    */
    .replace(/[\u2192\u27a1]/g, 'to')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00d7/g, 'x')
    // Anything still outside Latin-1 would take the same wrong path.
    .replace(/[\u0100-\uffff]/g, '');
}

/** A PNG's own proportions, so a plate is never stretched. */
const measured = new Map<string, Promise<{ w: number; h: number }>>();

function measure(dataUrl: string): Promise<{ w: number; h: number }> {
  // Asked twice for every plate - once to find out whether it fits on this
  // page, once to draw it - and decoding a map PNG is not free.
  const seen = measured.get(dataUrl);
  if (seen) return seen;

  const size = new Promise<{ w: number; h: number }>((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 16, h: 9 });
    img.src = dataUrl;
  });
  measured.set(dataUrl, size);

  return size;
}

const INK = {
  brand: [29, 111, 106] as [number, number, number],
  paper: [255, 255, 255] as [number, number, number],
  wash: [240, 244, 243] as [number, number, number],
  rule: [214, 222, 220] as [number, number, number],
  body: [32, 38, 37] as [number, number, number],
  quiet: [110, 122, 120] as [number, number, number],
};

async function asPdf({ days, name }: ExportSource): Promise<Blob> {
  /*
    Loaded when somebody asks for a PDF, not when the app starts.

    jsPDF is the one format here that needs a library at all, and most people
    exporting a route never ask for this one. A dynamic import keeps it out
    of the bundle everybody downloads.
  */
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 42;
  const width = pageW - margin * 2;
  const floor = pageH - 56;
  let y = margin;

  const setInk = (c: [number, number, number]) => doc.setTextColor(c[0], c[1], c[2]);
  const setFill = (c: [number, number, number]) => doc.setFillColor(c[0], c[1], c[2]);

  /** Starts a fresh page when the next block would not fit on this one. */
  const room = (needed: number) => {
    if (y + needed <= floor) return false;
    doc.addPage();
    y = margin;

    return true;
  };

  const write = (
    text: string,
    { size = 9.5, style = 'normal', gap = 13, indent = 0, colour = INK.body }: {
      size?: number;
      style?: 'normal' | 'bold' | 'italic';
      gap?: number;
      indent?: number;
      colour?: [number, number, number];
    } = {},
  ) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    setInk(colour);
    const lines = doc.splitTextToSize(safe(text), width - indent) as string[];
    room(lines.length * gap);
    doc.text(lines, margin + indent, y);
    y += lines.length * gap;
  };

  /**
   * A real arrow, drawn rather than typed.
   *
   * The standard PDF fonts have no arrow glyph, and the two-byte encoding
   * jsPDF falls back to for one cannot be drawn by them either - which is how
   * "S -> 1" came to be the honest option. Three lines are not a compromise:
   * at this size they are crisper than a glyph, and they cost nothing.
   */
  const arrow = (x: number, mid: number, len = 13) => {
    doc.setDrawColor(INK.brand[0], INK.brand[1], INK.brand[2]);
    doc.setLineWidth(1.1);
    doc.line(x, mid, x + len, mid);
    doc.line(x + len - 4.5, mid - 3.2, x + len, mid);
    doc.line(x + len - 4.5, mid + 3.2, x + len, mid);
  };

  /**
   * One maneuver, drawn in a small square, the way a satnav shows it.
   *
   * Vector lines rather than an icon font: the standard PDF fonts have no
   * such glyphs, embedding one would cost more than the rest of the document,
   * and at this size three or four strokes read more clearly than a typeface
   * would. `cy` is the middle of the line of text it belongs to.
   */
  const turnIcon = (shape: TurnShape, cx: number, cy: number, s = 4.2) => {
    doc.setDrawColor(INK.brand[0], INK.brand[1], INK.brand[2]);
    doc.setFillColor(INK.brand[0], INK.brand[1], INK.brand[2]);
    doc.setLineWidth(0.9);
    doc.setLineCap('round');
    doc.setLineJoin('round');

    /** An arrowhead at a point, pointing along a direction in radians. */
    const head = (x: number, yy: number, angle: number) => {
      const len = s * 0.55;
      const wing = 0.6;
      doc.line(x, yy, x - len * Math.cos(angle - wing), yy - len * Math.sin(angle - wing));
      doc.line(x, yy, x - len * Math.cos(angle + wing), yy - len * Math.sin(angle + wing));
    };

    const up = Math.PI / 2;

    switch (shape) {
      case 'start':
        doc.circle(cx, cy, s * 0.38, 'F');
        break;

      case 'finish':
        doc.circle(cx, cy, s * 0.5, 'S');
        doc.circle(cx, cy, s * 0.2, 'F');
        break;

      case 'left':
      case 'right': {
        const dir = shape === 'right' ? 1 : -1;
        doc.line(cx - dir * s * 0.3, cy + s, cx - dir * s * 0.3, cy - s * 0.35);
        doc.line(cx - dir * s * 0.3, cy - s * 0.35, cx + dir * s * 0.65, cy - s * 0.35);
        head(cx + dir * s * 0.65, cy - s * 0.35, dir > 0 ? 0 : Math.PI);
        break;
      }

      case 'slight-left':
      case 'slight-right': {
        const dir = shape === 'slight-right' ? 1 : -1;
        doc.line(cx - dir * s * 0.35, cy + s, cx - dir * s * 0.35, cy + s * 0.1);
        doc.line(cx - dir * s * 0.35, cy + s * 0.1, cx + dir * s * 0.55, cy - s * 0.8);
        head(cx + dir * s * 0.55, cy - s * 0.8, dir > 0 ? Math.PI / 4 : (3 * Math.PI) / 4);
        break;
      }

      case 'sharp-left':
      case 'sharp-right': {
        const dir = shape === 'sharp-right' ? 1 : -1;
        doc.line(cx - dir * s * 0.3, cy + s, cx - dir * s * 0.3, cy - s * 0.45);
        doc.line(cx - dir * s * 0.3, cy - s * 0.45, cx + dir * s * 0.65, cy + s * 0.15);
        head(cx + dir * s * 0.65, cy + s * 0.15, dir > 0 ? -Math.PI / 5 : Math.PI + Math.PI / 5);
        break;
      }

      case 'uturn':
        doc.line(cx + s * 0.5, cy + s, cx + s * 0.5, cy - s * 0.25);
        doc.lines(
          [[0, -s * 0.5], [-s, 0], [0, s * 0.5]],
          cx + s * 0.5, cy - s * 0.25, [1, 1], 'S', false,
        );
        doc.line(cx - s * 0.5, cy - s * 0.25, cx - s * 0.5, cy + s * 0.55);
        head(cx - s * 0.5, cy + s * 0.55, -up);
        break;

      case 'roundabout':
        doc.circle(cx - s * 0.1, cy + s * 0.05, s * 0.5, 'S');
        doc.line(cx - s * 0.1, cy + s, cx - s * 0.1, cy + s * 0.55);
        doc.line(cx + s * 0.25, cy - s * 0.3, cx + s * 0.75, cy - s * 0.8);
        head(cx + s * 0.75, cy - s * 0.8, Math.PI / 4);
        break;

      case 'ferry':
        doc.line(cx - s * 0.75, cy + s * 0.6, cx + s * 0.75, cy + s * 0.6);
        doc.line(cx - s * 0.5, cy + s * 0.6, cx, cy);
        doc.line(cx + s * 0.5, cy + s * 0.6, cx, cy);
        doc.line(cx, cy, cx, cy - s * 0.85);
        break;

      case 'straight':
      default:
        doc.line(cx, cy + s, cx, cy - s * 0.5);
        head(cx, cy - s * 0.5, up);
        break;
    }
  };

  /** How tall a plate will come out, asked before the page is committed. */
  const plateHeight = async (dataUrl: string, maxHeight: number) => {
    const { w, h } = await measure(dataUrl);

    return Math.min((width * h) / w, maxHeight);
  };

  /** A plate, scaled to the column and never taller than the page allows. */
  const plate = async (dataUrl: string, maxHeight: number) => {
    const { w, h } = await measure(dataUrl);
    /*
      Full column width, with the height following.

      Capping the height first made a wide map into a narrow one floating in
      white space: the cap is a last resort against a tall capture pushing a
      page over, not the thing that decides the size.
    */
    let drawW = width;
    let drawH = (width * h) / w;
    if (drawH > maxHeight) {
      drawH = maxHeight;
      drawW = (maxHeight * w) / h;
    }
    room(drawH + 10);
    doc.addImage(dataUrl, 'PNG', margin, y, drawW, drawH, undefined, 'FAST');
    doc.setDrawColor(INK.rule[0], INK.rule[1], INK.rule[2]);
    doc.setLineWidth(0.5);
    doc.rect(margin, y, drawW, drawH);
    y += drawH + 12;
  };

  // ── masthead ──────────────────────────────────────────────────
  setFill(INK.brand);
  doc.rect(0, 0, pageW, 104, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setInk(INK.paper);
  const titleLines = (doc.splitTextToSize(safe(name), width) as string[]).slice(0, 2);
  doc.text(titleLines, margin, 44);

  const first = days[0];
  const manyDays = days.length > 1;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    safe(
      (manyDays
        ? `${days.length} DAYS  ·  `
          + `${minutes(days.reduce((n, d) => n + d.plan.totals.totalMinutes, 0))}  ·  `
          + `${km(days.reduce((n, d) => n + d.plan.totals.distanceMeters, 0))}  ·  `
        : `${first?.plan.mode.toUpperCase() ?? ''}  ·  `
          + `departs ${clock(first?.plan.startTime ?? '')}  ·  `)
      + `planned ${first ? new Date(first.plan.generatedAt).toLocaleString() : ''}`,
    ),
    margin,
    44 + titleLines.length * 20 + 4,
  );
  y = 104 + 24;

  /*
    Every planned day, one after another.

    A day a page, because an exported round is read in a vehicle: leafing
    back past Tuesday to find where Wednesday starts is the one thing a
    printed itinerary must not ask for.
  */
  for (let dayNo = 0; dayNo < days.length; dayNo += 1) {
    const { label, plan, directions, images } = days[dayNo];

    if (dayNo > 0) {
      doc.addPage();
      y = margin;
    }

    if (manyDays) {
      setFill(INK.brand);
      doc.rect(margin, y, width, 22, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      setInk(INK.paper);
      doc.text(safe(label), margin + 10, y + 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(
        safe(`${plan.mode} · departs ${clock(plan.startTime)}`),
        margin + width - 10, y + 15, { align: 'right' },
      );
      y += 34;
    }

    // ── the figures ───────────────────────────────────────────────
    const figures: [string, string][] = [
      ['TOTAL', minutes(plan.totals.totalMinutes)],
      ['TRAVEL', minutes(plan.totals.travelMinutes)],
      ['AT STOPS', minutes(plan.totals.dwellMinutes)],
      ['DISTANCE', km(plan.totals.distanceMeters)],
      ['STOPS', String(Math.max(plan.stops.length - 2, 0))],
    ];
    const cellW = width / figures.length;
    setFill(INK.wash);
    doc.rect(margin, y, width, 44, 'F');
    figures.forEach(([label, value], i) => {
      const x = margin + cellW * i + 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      setInk(INK.quiet);
      doc.text(label, x, y + 16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      setInk(INK.body);
      doc.text(safe(value), x, y + 33);
    });
    y += 60;

    plan.warnings.forEach((w) => write(w, { size: 8.5, style: 'italic', colour: INK.quiet }));
    if (plan.warnings.length > 0) y += 6;

    // ── the whole round, as a picture ─────────────────────────────
    if (images?.overview) {
      write('THE ROUND', { size: 8, style: 'bold', gap: 14, colour: INK.quiet });
      await plate(images.overview, 430);
    }

    // ── the itinerary, as a table ─────────────────────────────────
    write('ITINERARY', { size: 8, style: 'bold', gap: 16, colour: INK.quiet });

    const cols = [26, width - 26 - 70 - 56 - 62, 70, 56, 62];
    const heads = ['', 'PLACE', 'ARRIVE', 'STAY', 'LEAVE'];
    const rowX = (n: number) => margin + cols.slice(0, n).reduce((a, b) => a + b, 0);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    setInk(INK.quiet);
    heads.forEach((h, i) => doc.text(h, rowX(i) + 4, y));
    y += 6;
    doc.setDrawColor(INK.rule[0], INK.rule[1], INK.rule[2]);
    doc.setLineWidth(0.7);
    doc.line(margin, y, margin + width, y);
    y += 4;

    plan.stops.forEach((stop, i) => {
      const nameLines = (() => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);

        return doc.splitTextToSize(safe(stop.name), (cols[1] ?? 0) - 8) as string[];
      })();
      const rowH = Math.max(18, nameLines.length * 11 + 7);

      if (room(rowH)) {
        doc.setDrawColor(INK.rule[0], INK.rule[1], INK.rule[2]);
        doc.line(margin, y, margin + width, y);
        y += 4;
      }
      if (i % 2 === 1) {
        setFill(INK.wash);
        doc.rect(margin, y - 3, width, rowH, 'F');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setInk(INK.brand);
      doc.text(stopMark(plan, i), rowX(0) + 4, y + 9);

      doc.setFont('helvetica', 'normal');
      setInk(INK.body);
      doc.text(nameLines, rowX(1) + 4, y + 9);

      const first = i === 0;
      const last = i === plan.stops.length - 1;
      doc.setFontSize(8.5);
      setInk(INK.quiet);
      doc.text(first ? '-' : clock(stop.arrivalTime), rowX(2) + 4, y + 9);
      doc.text(first || last ? '-' : minutes(stop.dwellMinutes), rowX(3) + 4, y + 9);
      doc.text(last ? '-' : clock(stop.departureTime), rowX(4) + 4, y + 9);

      y += rowH;
    });

    // ── the directions ────────────────────────────────────────────
    doc.addPage();
    y = margin;
    write('DIRECTIONS', { size: 8, style: 'bold', gap: 18, colour: INK.quiet });

    for (let i = 0; i < plan.legs.length; i += 1) {
      const leg = plan.legs[i];
      if (!leg) continue;
      const from = plan.stops[leg.fromIndex];
      const to = plan.stops[leg.toIndex];

      /*
        The header, its map and the first turns, kept on one page.

        `room` was being asked about the header alone, so a leg could put its
        title at the foot of a page and its map at the top of the next: a
        heading with nothing under it, and a picture with nothing to say what
        it is of.
      */
      const picture = images?.legs[i];
     
      const plateH = picture ? await plateHeight(picture, 430) : 0;
      room(38 + (plateH > 0 ? plateH + 12 : 0) + 26);

      setFill(INK.wash);
      doc.rect(margin, y - 2, width, 28, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setInk(INK.brand);
      const fromMark = safe(stopMark(plan, leg.fromIndex));
      const toMark = safe(stopMark(plan, leg.toIndex));
      const baseline = y + 16;
      doc.text(fromMark, margin + 8, baseline);
      const arrowX = margin + 8 + doc.getTextWidth(fromMark) + 6;
      arrow(arrowX, baseline - 3);
      const toX = arrowX + 13 + 6;
      doc.text(toMark, toX, baseline);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setInk(INK.body);
      doc.text(
        safe(`${from?.name ?? ''} to ${to?.name ?? ''}`),
        toX + doc.getTextWidth(toMark) + 14,
        baseline,
        { maxWidth: width - 210 },
      );
      doc.setFontSize(8.5);
      setInk(INK.quiet);
      doc.text(
        safe(`${km(leg.distanceMeters)} · ${minutes(leg.durationMinutes)} by ${leg.mode}`),
        margin + width - 8,
        y + 16,
        { align: 'right' },
      );
      y += 38;

      // One camera, one plate at a time: these cannot be taken in parallel.
     
      if (picture) await plate(picture, 430);

      const turns = turnsFor(directions, i);
      if (turns.length === 0) {
        write('No turn-by-turn for this leg.', { size: 8.5, style: 'italic', colour: INK.quiet, indent: 10 });
      } else {
        turns.forEach((m, n) => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          const textX = margin + 46;
          const lines = doc.splitTextToSize(
            safe(m.instruction), margin + width - textX - 46,
          ) as string[];
      /*
        Room for the icon, not just for the words.

        At one line per 11pt the drawings ran into each other top to bottom and
        read as one long chain rather than a turn each. The icon is half the
        height it was and the line is a little taller than the text needs.
      */
          const rowH = Math.max(lines.length * 11.5, 13.5);
          room(rowH + 2);

          setInk(INK.quiet);
          doc.text(`${n + 1}.`, margin + 8, y);
          // Centred on the first line of the instruction, not on the block.
          turnIcon(shapeOf(m.type), margin + 32, y - 2.8);

          setInk(INK.body);
          doc.text(lines, textX, y);
          if (m.distanceMeters) {
            setInk(INK.quiet);
            doc.text(safe(km(m.distanceMeters)), margin + width, y, { align: 'right' });
          }
          y += rowH;
        });
      }
      y += 16;
    }

    // ── what it kept clear of ─────────────────────────────────────
    if (plan.avoidedZones.length > 0) {
      room(50);
      y += 4;
      write(
        `KEPT CLEAR OF (${plan.avoidedZones.length})`,
        { size: 8, style: 'bold', gap: 16, colour: INK.quiet },
      );

      /*
        All of them, in two columns.

        An avoided CATEGORY becomes a zone per place, so "no museums" can be
        sixty-eight named museums. Printing a count instead of the names was the
        wrong trade: this list is the record of what the route was told to keep
        away from, and a reader checking whether a particular place was covered
        cannot check a number. Two columns keep it to about a page.
      */
      const colW = (width - 18) / 2;
      const names = plan.avoidedZones.map(
        (z) => `${z.name}${z.violated ? '  (not honoured)' : ''}`,
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setInk(INK.body);

      let col = 0;
      let colY = y;
      for (const name of names) {
        const lines = doc.splitTextToSize(safe(name), colW - 10) as string[];
        const needed = lines.length * 11;

        if (colY + needed > floor) {
          if (col === 0) {
            // Down the first column, then the second, then over the page.
            col = 1;
            colY = y;
          } else {
            doc.addPage();
            col = 0;
            y = margin;
            colY = margin;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            setInk(INK.body);
          }
        }

        doc.text(lines, margin + 10 + col * (colW + 18), colY);
        colY += needed;
      }
      y = col === 1 ? Math.max(colY, floor) : colY;
    }

  }

  // ── running feet ──────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(INK.rule[0], INK.rule[1], INK.rule[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, pageH - 40, pageW - margin, pageH - 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setInk(INK.quiet);
    doc.text(safe(name), margin, pageH - 28, { maxWidth: width - 120 });
    doc.text(`${p} / ${pages}`, pageW - margin, pageH - 28, { align: 'right' });
  }

  return doc.output('blob');
}

// ── handing it over ─────────────────────────────────────────────────

/** Builds the file and gives it to the browser to save. */
export async function exportRoute(format: ExportFormat, source: ExportSource): Promise<void> {
  const blob = format === 'pdf'
    ? await asPdf(source)
    : new Blob([
      format === 'gpx' ? asGpx(source)
        : format === 'gpxroute' ? asGpx(source, 'route')
          : format === 'geojson' ? asGeoJson(source)
            : format === 'markdown' ? asMarkdown(source)
              : asJson(source),
    ], { type: MEDIA[format] });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileNameFor(source.name, format);
  document.body.appendChild(link);
  link.click();
  link.remove();
  /*
    Revoked on the next turn of the loop, not immediately: Safari has not
    started reading the blob when click() returns, and freeing it there gives
    an empty file.
  */
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
